import { useEffect, useState } from 'react'

/**
 * Shared sticky-action-bar measurement — the reusable layout mechanism that
 * keeps floating utility widgets (Live Visitors pill, future FABs) clear of
 * sticky Save/Publish bars.
 *
 * Every StickyFooter registers its element here. Heights are measured with
 * ResizeObserver (so wrapping buttons, keyboard-driven viewport changes and
 * safe-area padding are all reflected live), published as
 *   --mobile-sticky-actions-height
 * on <html>, and exposed to React via useStickyActionsHeight().
 *
 * Z-INDEX SCALE (admin):
 *   page content ......... auto
 *   floating widgets ..... z-10  (Live Visitors pill)
 *   sticky page actions .. z-20  (StickyFooter — always above widgets)
 *   drawers / dialogs .... z-30  (overlay + content, see _drawer/_dialog.css)
 *   dropdown portals ..... 9999  (react-select menuPortal)
 *   toasts / alerts ...... top
 */

const bars = new Map() // element -> height
const listeners = new Set()
let current = 0

const publish = () => {
    const next = Math.max(0, ...bars.values())
    if (next === current) return
    current = next
    document.documentElement.style.setProperty(
        '--mobile-sticky-actions-height',
        `${next}px`
    )
    listeners.forEach((fn) => fn(next))
}

/** Register a sticky action bar element. Returns an unregister fn. */
export const registerStickyActions = (el) => {
    if (!el || typeof ResizeObserver === 'undefined') return () => {}
    const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
            bars.set(entry.target, entry.target.offsetHeight || 0)
        }
        publish()
    })
    observer.observe(el)
    bars.set(el, el.offsetHeight || 0)
    publish()
    return () => {
        observer.disconnect()
        bars.delete(el)
        publish()
    }
}

/** Current sticky-actions height (0 when no bar is mounted). */
export const useStickyActionsHeight = () => {
    const [height, setHeight] = useState(current)
    useEffect(() => {
        setHeight(current)
        const fn = (h) => setHeight(h)
        listeners.add(fn)
        return () => listeners.delete(fn)
    }, [])
    return height
}
